import AppButton from '@nxsys/components/AppButton';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppSelect from '@nxsys/components/AppSelect';
import AppSelectSync from '@nxsys/components/AppSelectSync';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { useGeneratePaymentFile } from '@nxsys/hooks/api/dashboard';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { dashboardActions } from '@nxsys/store/slices/dashboard/reducer/dashboardSlice';
import { GetPaymentFileListParams } from '@nxsys/store/slices/dashboard/types';
import { Option } from '@nxsys/types/models/Common';
import { ICompany } from '@nxsys/types/models/company';
import { IPayroll } from '@nxsys/types/models/payroll';
import { getAppConfig, showCurrencyByCountry } from '@nxsys/utils';
import { showMessage } from '@nxsys/utils/message';
import { formatMoney } from '@nxsys/utils/number-helper';
import { Tag } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { debounce, sumBy, toNumber } from 'lodash';
import { exportBankFile } from '@nxsys/utils/bank-file-download';
import { GeneratePaymentFileValues } from 'modules/reports/bankFile/components/GeneratePaymentFileModal';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import GeneratePaymentFileModal from './GeneratePaymentFileModal';
import { PAYMENT_MODEL_ENUM } from '@nxsys/constants/AppEnums';
import {
  getIrelandPaymentModels,
  getIrelandPaymentModelOptions,
} from '@nxsys/constants/AppConstants';
import { IRELAND, UNITED_KINGDOM } from '@nxsys/store/constants/keys';

type Props = {
  isOpen: boolean;
  toggle: () => void;
  cbAfterAction?: () => void;
};

const PaymentFileModal: React.FC<Props> = ({
  isOpen,
  toggle,
  cbAfterAction = () => {},
}) => {
  const { messages } = useTypedIntl();
  const dispatch = useDispatch();
  const paymentModels = getAppConfig('paymentModels');
  const { payrollCountry, isBureau } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isUK = isBureau && payrollCountry === UNITED_KINGDOM;
  // Country-only: this modal is reachable from both bureau dashboards and
  // DashboardUmbrella's admin Payments widget, so `isBureau &&` silently
  // dropped the CIS/PAYEE filter for an umbrella-Ireland tenant.
  const isIreland = payrollCountry === IRELAND;
  const { paymentFileList } = useSelector(
    (state: AppState) => state.dashboardStore,
  );
  const { list, pagination, loading, queryParams } = paymentFileList;
  const [selectedRows, setSelectedRows] = useState<IPayroll[] | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

  const [selectedPaymentModel, setSelectedPaymentModel] =
    useState<Option<string> | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<ICompany | null>(null);
  const [selectedAgency, setSelectedAgency] = useState<IAgency[] | null>(null);

  const [isOpenGeneratePaymentFileModal, setIsOpenGeneratePaymentFileModal] =
    useState(false);
  // The filter inputs keep their own internal value, so bumping this key on
  // close remounts them and stops them from displaying a selection we just
  // cleared.
  const [filtersKey, setFiltersKey] = useState(0);

  const clearSelectedRows = () => {
    setSelectedRows(null);
    setSelectedRowKeys([]);
  };

  // This modal stays mounted between openings, so nothing resets itself: a
  // company left over from a previous session kept feeding a payment provider
  // that no longer belonged to it, and Generate then sent empty bank details
  // ("Bank information is required!" 400).
  useEffect(() => {
    if (isOpen) return;
    clearSelectedRows();
    setSelectedPaymentModel(null);
    setSelectedCompany(null);
    setSelectedAgency(null);
    setIsOpenGeneratePaymentFileModal(false);
    setFiltersKey((key) => key + 1);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      // Rows picked under the previous filters are not in the incoming list
      clearSelectedRows();
      handleFetchData({
        page: 0,
        size: 20,
        paymentModels: selectedPaymentModel?.value
          ? [selectedPaymentModel.value]
          : undefined,
        companyPaymentId: selectedCompany?.id ?? undefined,
        agencyRefs: selectedAgency?.map((item: any) => item.id) ?? undefined,
      });
    }
  }, [isOpen, selectedPaymentModel, selectedCompany, selectedAgency]);

  const handleFetchData = (params: GetPaymentFileListParams) => {
    const newParams = {
      ...params,
      isPaymentView: true,
    };
    if (isUK) {
      newParams.paymentModels = [PAYMENT_MODEL_ENUM.PAYEE];
    }
    if (isIreland) {
      // Allow-list, not an override: honour the user's pick when they made
      // one, otherwise fall back to every model this tenant can hold. The
      // unconditional assignment predates umbrella-Ireland and was harmless
      // while isIreland implied bureau, where no picker is rendered.
      const allowed = getIrelandPaymentModels(isBureau);
      const picked = params.paymentModels?.filter((m) =>
        allowed.includes(m as PAYMENT_MODEL_ENUM),
      );
      newParams.paymentModels = picked?.length ? picked : allowed;
    }
    dispatch(dashboardActions.getPaymentFileList(newParams));
  };

  const refetchData = () => {
    handleFetchData({ ...queryParams });
    cbAfterAction();
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const handleSearch = useCallback(
    debounce((e: React.ChangeEvent<HTMLInputElement>) => {
      clearSelectedRows();
      handleFetchData({ ...queryParams, search: e.target.value });
    }, 500),
    [queryParams],
  );

  const bureauColumns: ColumnsType<IPayroll> = [
    {
      key: 'id',
      title: 'ID',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (record) => (
        <span>
          {record.id || '_'}
          {record?.isPAL && (
            <Tag color="warning" className="ml-1">PAL</Tag>
          )}
        </span>
      ),
    },
    {
      key: 'subcontractorRef',
      title: 'Employee ID',
      dataIndex: 'subcontractorRef',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) => value?.id || '_',
    },
    {
      key: 'subcontractorName',
      title: 'Employee Name',
      dataIndex: 'subcontractorName',
    },
    {
      key: 'companyRef',
      title: 'Employer Name',
      dataIndex: 'companyRef',
      render: (value) => value?.companyName || '_',
    },
    {
      key: 'net',
      title: 'Net Pay',
      dataIndex: 'net',
      align: 'right',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (props) => (
        <span>{formatMoney(props, showCurrencyByCountry(payrollCountry))}</span>
      ),
    },
    {
      key: 'accountName',
      title: 'Account Name',
      dataIndex: 'subcontractorRef',
      render: (value) => value?.bankRef?.accountName || '_',
    },
    {
      key: 'accountNumber',
      title: 'Account Number',
      dataIndex: 'subcontractorRef',
      render: (value) => value?.bankRef?.accountNumber || '_',
    },
    {
      key: 'sortCode',
      title: 'Sort Code',
      dataIndex: 'subcontractorRef',
      render: (value) => value?.bankRef?.sortCode || '_',
    },
  ];

  const umbrellaColumns: ColumnsType<IPayroll> = [
    {
      key: 'id',
      title: 'ID',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      width: 160,
      render: (record) => (
        <span>
          {record.id || '_'}
          {record?.isPAL && (
            <Tag color="warning" className="ml-1">PAL</Tag>
          )}
        </span>
      ),
    },
    {
      key: 'taxPeriod',
      title: 'Tax Period',
      align: 'right',
      render: (record) =>
        `${record?.taxPeriod || ''}/${
          record?.taxYear || record?.currentYear || ''
        }`,
      sorter: true,
      width: 160,
      sortDirections: ['descend', 'ascend'],
    },
    {
      key: 'subcontractorRef',
      title: 'Employee ID',
      dataIndex: 'subcontractorRef',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (value) => value?.id || '_',
    },
    {
      key: 'subcontractorRef',
      title: 'Agency Ref',
      dataIndex: 'subcontractorRef',
      width: 250,
      render: (value) =>
        `${value?.primaryAgency?.agencyPrefix || '___'}/${
          value?.agencyRef || value?.systemId || '_'
        }`,
    },
    {
      key: 'agencyName',
      title: 'Agency Name',
      dataIndex: 'agencyName',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      width: 350,
    },
    {
      key: 'subcontractorName',
      title: 'Employee Name',
      width: 200,
      render: (value) =>
        value?.subcontractorName ||
        `${value?.subcontractorRef?.forename || ''} ${
          value?.subcontractorRef?.surname || ''
        }`,
    },
    {
      key: 'timesheetValue',
      title: 'Timesheet Value',
      dataIndex: 'timesheetValue',
      align: 'right',
      width: 200,
      render: (props) => (
        <span>{formatMoney(props, showCurrencyByCountry(payrollCountry))}</span>
      ),
    },
    {
      key: 'margin',
      title: 'Margin',
      dataIndex: 'margin',
      align: 'right',
      render: (props) => (
        <span>{formatMoney(props, showCurrencyByCountry(payrollCountry))}</span>
      ),
      sorter: true,
      width: 150,
      sortDirections: ['descend', 'ascend'],
    },
    {
      key: 'gross',
      title: 'Gross',
      dataIndex: 'gross',
      align: 'right',
      render: (props) => (
        <span>{formatMoney(props, showCurrencyByCountry(payrollCountry))}</span>
      ),
      sorter: true,
      width: 150,
      sortDirections: ['descend', 'ascend'],
    },
    {
      key: 'materials',
      title: 'Materials',
      dataIndex: 'materials',
      align: 'right',
      render: (props) => (
        <span>{formatMoney(props, showCurrencyByCountry(payrollCountry))}</span>
      ),
      sorter: true,
      width: 150,
      sortDirections: ['descend', 'ascend'],
    },
    {
      key: 'vat',
      title: 'VAT',
      dataIndex: 'vat',
      align: 'right',
      render: (props) => (
        <span>{formatMoney(props, showCurrencyByCountry(payrollCountry))}</span>
      ),
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      width: 150,
    },
    {
      key: 'net',
      title: 'Net',
      dataIndex: 'net',
      align: 'right',
      render: (props) => (
        <span>{formatMoney(props, showCurrencyByCountry(payrollCountry))}</span>
      ),
      sorter: true,
      width: 150,
      sortDirections: ['descend', 'ascend'],
    },
    {
      key: 'cisStatus',
      title: 'CIS Status',
      dataIndex: 'cisStatus',
      align: 'center',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
    },
    {
      key: 'cisDeduction',
      title: 'CIS Deduction (amount)',
      dataIndex: 'cisDeduction',
      align: 'right',
      width: 250,
      render: (props) => (
        <span>{formatMoney(props, showCurrencyByCountry(payrollCountry))}</span>
      ),
      sorter: true,
      sortDirections: ['descend', 'ascend'],
    },
    {
      key: 'otherDeduction',
      title: 'Other Deduction',
      dataIndex: 'otherDeduction',
      align: 'right',
      render: (props) => (
        <span>{formatMoney(props, showCurrencyByCountry(payrollCountry))}</span>
      ),
      sorter: true,
      sortDirections: ['descend', 'ascend'],
    },
    {
      key: 'invoiceNo',
      title: 'Sales Inv Ref',
      dataIndex: 'invoiceNo',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
    },
  ];

  const columns = isBureau ? bureauColumns : umbrellaColumns;

  const handleExportBankFile = async (res: any, bankName?: string) => {
    if (await exportBankFile(res, bankName)) {
      showMessage({
        type: 'success',
        content: messages['common.paymentFileGeneratedSuccessfully'],
      });
      refetchData();
    } else {
      showMessage({
        type: 'error',
        content: res?.error || messages['common.generatePaymentFileFailed'],
      });
    }
  };

  const { run: generatePaymentFile } = useGeneratePaymentFile();
  const handleGeneratePaymentFile = async (
    values: GeneratePaymentFileValues,
  ) => {
    try {
      if (!selectedRows || selectedRows?.length === 0 || !selectedCompany)
        return;
      const provider = selectedCompany?.banksList?.find(
        (bank) => bank.id === toNumber(values.paymentProvider?.value),
      );
      // Without a match every bank field would go out empty and the API would
      // reject the whole request
      if (!provider) {
        showMessage({
          type: 'error',
          content: messages['common.generatePaymentFileFailed'],
        });
        return;
      }
      const res = await generatePaymentFile(selectedCompany.id, {
        accountName: provider?.accountName || '',
        accountNumber: provider?.accountNumber || '',
        bankName: provider?.bankName || '',
        sortCode: provider?.sortCode || '',
        paymentDate: values.paymentDate
          ? values.paymentDate.format('DD/MM/YYYY')
          : null,
        payrollIds: selectedRows.map((item) => item.id),
      });
      handleExportBankFile(res, provider?.bankName);
    } catch (error) {
      console.error(error);
    }
  };
  const handleOpenGeneratePaymentFileModal = () => {
    if (!selectedRows || selectedRows?.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    setIsOpenGeneratePaymentFileModal(true);
  };

  return (
    <AppModalContainer
      title={
        <div className="flex items-center justify-between w-full gap-x-[8px]">
          <div className="whitespace-nowrap">Payment Files</div>
          <AppButton
            name="Generate Payment File"
            disabled={!selectedCompany}
            onClick={handleOpenGeneratePaymentFileModal}
          />
        </div>
      }
      isOpen={isOpen}
      toggle={toggle}
      hiddenFooter
      className="max-sm:w-[100vw]"
      width={1200}
      minHeight="0"
      noPaddingBody
      titleFullWidth
    >
      <AppsContent>
        <div className="flex items-center justify-between gap-2 w-full my-[20px] px-[10px] flex-wrap">
          <div className="flex items-center gap-2 flex-1 flex-wrap">
            {!isBureau && (
              <AppSelect
                key={`payment-model-${filtersKey}`}
                options={
                  (isIreland
                    ? getIrelandPaymentModelOptions(isBureau)
                    : paymentModels) as any
                }
                placeholder="Payment Model"
                className="!min-w-0 !w-fit"
                onChange={(value) => setSelectedPaymentModel(value)}
              />
            )}
            <AppSelectSync
              key={`company-${filtersKey}`}
              type="company"
              apiType="searchCompany"
              placeholder={isBureau ? 'Select Employer' : 'Select Payment Company'}
              onChangeValue={(value: any) => {
                setSelectedCompany(value?.fullData);
              }}
              paramFilter={
                isUK ? { paymentModalEmployee: PAYMENT_MODEL_ENUM.PAYEE } : {}
              }
              className="!min-w-[250px]"
            />
            {!isBureau && (
              <AppSelectSync
                key={`agency-${filtersKey}`}
                type="agency"
                apiType="searchAgency"
                placeholder="Select Agency"
                mode="multiple"
                onChangeValue={(value: any) => {
                  setSelectedAgency(value?.map((item: any) => item.fullData));
                }}
                paramFilter={{ hasPaymentFileNeedConfirm: true }}
                className="!min-w-[165px]"
              />
            )}
          </div>
          <div className="flex items-center gap-2">
            <AppInputSearch
              key={`search-${filtersKey}`}
              onChange={handleSearch}
            />
          </div>
        </div>
        <AppTableContainer
          rowKey="id"
          data={list ?? []}
          loading={loading}
          page={pagination.current}
          total={pagination.totalDocs}
          pageSize={pagination.pageSize}
          scroll={{ x: isBureau ? 1800 : 3500 }}
          columns={columns}
          onChangePagination={onChangePagination}
          rowSelection={{
            type: 'checkbox',
            selectedRowKeys,
            onChange: (keys: React.Key[], selectedRows: IPayroll[]) => {
              setSelectedRowKeys(keys);
              setSelectedRows(selectedRows);
            },
          }}
        />
      </AppsContent>
      {isOpenGeneratePaymentFileModal && selectedCompany && (
        <GeneratePaymentFileModal
          isOpen={isOpenGeneratePaymentFileModal}
          toggle={() => setIsOpenGeneratePaymentFileModal(false)}
          onGenerate={handleGeneratePaymentFile}
          company={selectedCompany}
          totalPayment={sumBy(selectedRows, (item) => Number(item?.net || 0))}
        />
      )}
    </AppModalContainer>
  );
};

export default PaymentFileModal;
